/**
 * The in-flight map's data model: one baked `.WLD` region plus the mission
 * being flown plus where the aeroplane is, reduced to plain points and labels
 * in a single flat 2D frame. Pure — no fetch, no canvas — so every position on
 * the map is assertable in a unit test.
 *
 * **Map space.** `u` is world metres east along `.WLD` `x`; `v` is world metres
 * along `.WLD` `y`, drawn *up* the map. Both are measured in the same
 * recentred frame the sim and the flight scene share, so the player's own
 * `position.x`/`position.z` land on the map with no conversion beyond the sign:
 * `world/placement-transform.ts` sends `.WLD` `y` to three's `−Z`, so
 * `v = −position.z` is that mapping read backwards. Every other thing on the
 * map — a mission waypoint, the front line, an aerodrome — is already in one of
 * those two frames, which is what makes one flat space enough.
 *
 * Which way up is *north* is still UNKNOWN (`world/placement-transform.ts` has
 * never resolved it and neither does this), so the map's compass rose points at
 * `+v` and calls it the region's own up — the same choice `/assets`' World
 * viewer already made, and the two panes agree because they use the same
 * projection.
 */

import { horizontalDistanceM, playerFlightOf, type MissionRegion, type MissionSpec, type Vec3 } from "@red-opus/engine";
import { projectAerodromes, regionOrigin } from "../world/aerodrome-projection.ts";
import type { RegionGround } from "../world/region-ground.ts";
import type { PlacementOrigin } from "../world/placement-transform.ts";
import type { WorldRegionBody } from "../world/region-loader.ts";
import { wldUnitsToMetres, WLD_UNITS_PER_METRE } from "../world/wld-units.ts";
import type { WldPoint } from "./sector-code.ts";

export interface MapPoint {
  readonly u: number;
  readonly v: number;
}

export interface MapMarker extends MapPoint {
  readonly label: string;
}

export interface MapBounds {
  readonly minU: number;
  readonly maxU: number;
  readonly minV: number;
  readonly maxV: number;
}

/** Where the aeroplane is and which way its nose points — the one part of the model that changes while the map is open. */
export interface MapPlayer extends MapPoint {
  readonly headingRad: number;
}

/**
 * A hill worth drawing: one of the region's placed `.GI` ground surfaces that
 * is terrain rather than a building. The two are cleanly separated by extent —
 * across all five regions every building-sized ground object is at most 46 m
 * across and every terrain one is at least 1 575 m, a 34× gap with nothing in
 * it — so `HILL_MIN_EXTENT_M` is a threshold in the void rather than a fitted
 * cutoff. It selects exactly ten placements in the whole game: seven in Verdun
 * and three in Paris, the other three regions being flat.
 */
export interface MapHill extends MapPoint {
  /** Half-extent of the hill's footprint, in metres — the radius the map draws it at. */
  readonly radiusM: number;
}

export const HILL_MIN_EXTENT_M = 500;

export interface MapModel {
  /** `AERO.DAT`/`.WLD` display name, e.g. `"Verdun"`. */
  readonly regionName: string;
  readonly bounds: MapBounds;
  /** The recentring point, metres on the `.WLD` axes — what `wldPointOf` adds back to reach the sector grid's own frame. */
  readonly origin: PlacementOrigin;
  /** Every placement in the region: the stipple that reads as towns and scattered farms. */
  readonly scenery: readonly MapPoint[];
  readonly hills: readonly MapHill[];
  readonly aerodromes: readonly MapMarker[];
  /** The front, as the port models it: a line of constant `v` (`packages/engine`'s `FrontLine`). Absent on a flight with no mission. */
  readonly frontLineV: number | undefined;
  /** The player flight's briefed route, in order. */
  readonly waypoints: readonly MapMarker[];
  /** Static mission targets — the things a `TARGET` mark on a briefing map means. */
  readonly targets: readonly MapMarker[];
  readonly home: MapMarker | undefined;
}

/** World-metre position (the sim's frame) to map space. */
export function mapPointOfWorld(position: { readonly x: number; readonly z: number }): MapPoint {
  return { u: position.x, v: -position.z };
}

/** Map space back to the raw `.WLD` world units the sector grid is defined in. */
export function wldPointOf(model: MapModel, point: MapPoint): WldPoint {
  return {
    x: (point.u + model.origin.x) * WLD_UNITS_PER_METRE,
    y: (point.v + model.origin.z) * WLD_UNITS_PER_METRE,
  };
}

function boundsOf(body: WorldRegionBody, origin: PlacementOrigin): MapBounds {
  return {
    minU: wldUnitsToMetres(body.bounds.minX) - origin.x,
    maxU: wldUnitsToMetres(body.bounds.maxX) - origin.x,
    minV: wldUnitsToMetres(body.bounds.minY) - origin.z,
    maxV: wldUnitsToMetres(body.bounds.maxY) - origin.z,
  };
}

function hillsOf(body: WorldRegionBody, origin: PlacementOrigin): MapHill[] {
  const hills: MapHill[] = [];
  for (const placement of body.placements) {
    const ground = body.groundObjects[String(placement.objectId)];
    if (!ground) continue;
    const extent = Math.max(ground.extentXM, ground.extentYM);
    if (extent < HILL_MIN_EXTENT_M) continue;
    hills.push({
      u: wldUnitsToMetres(placement.x) - origin.x,
      v: wldUnitsToMetres(placement.y) - origin.z,
      radiusM: extent / 2,
    });
  }
  return hills;
}

/**
 * The one region with no front line printed on its sheet, and it is not a
 * judgement call: `MAP1.DAT` — London's own briefing map — is the only one of
 * the five that carries no front polyline in group 0, and
 * docs/formats/world-terrain.md §2.5 treats "London having none, and only
 * London, is itself the confirmation" as the evidence that group 0 *is* the
 * Western Front. London is the England/home-defence region
 * (docs/game/aces-squadrons.md §5), and there were no trenches in Essex.
 *
 * The engine still gives every mission a `FrontLine`, because it needs one to
 * answer "am I over friendly territory" for kill credit
 * (`packages/engine/src/mission/mission-world.ts`'s stated port-side
 * simplification). Nothing about that changes here — this only stops the sheet
 * printing a trench line across Kent.
 */
const REGIONS_WITHOUT_A_FRONT: ReadonlySet<MissionRegion> = new Set<MissionRegion>(["london"]);

/** The mission's own marks: the player flight's route, every static target, and the field it flew from. */
function missionMarks(mission: MissionSpec | undefined): Pick<MapModel, "waypoints" | "targets" | "home" | "frontLineV"> {
  if (!mission) return { waypoints: [], targets: [], home: undefined, frontLineV: undefined };
  const marker = (position: Vec3, label: string): MapMarker => ({ ...mapPointOfWorld(position), label });
  return {
    waypoints: (playerFlightOf(mission).waypoints ?? []).map((waypoint) => marker(waypoint.position, waypoint.label)),
    targets: mission.targets.map((target) => marker(target.position, target.label)),
    home: mission.homeBase ? marker(mission.homeBase.position, mission.homeBase.name) : undefined,
    frontLineV: REGIONS_WITHOUT_A_FRONT.has(mission.region) ? undefined : -mission.frontLine.zM,
  };
}

/**
 * `startAerodrome` must be the same one `flight/fly-region.ts` recentred the
 * scenery on (`world/region-catalog.ts`'s `REGION_BAKE`), or the map and the
 * world it is a map *of* would disagree by the distance between two fields.
 *
 * `ground` is `world/region-ground.ts`'s answer for this region — **pass it**.
 * Since `docs/engineering/london-sea-placements.md` the origin is no longer a
 * pure function of the region body: it depends on where the water is, which
 * needs every placed object's geometry, so it is worked out once
 * asynchronously and shared. Without it this falls back to the bare
 * bounding-box fit, which is the frame the sheet used before that document and
 * is *not* the frame `/fly` draws in. The fallback exists so a test that only
 * wants a sheet's geometry need not load a region's models; the real sheet
 * always passes one.
 */
export function buildMapModel(
  body: WorldRegionBody,
  startAerodrome: string,
  mission?: MissionSpec,
  ground?: RegionGround,
): MapModel {
  const origin = ground?.origin ?? regionOrigin(body.aerodromes, body.bounds, startAerodrome);
  const aerodromes = ground
    ? ground.aerodromes.map((field) => ({ label: field.name, u: field.x, v: -field.z }))
    : projectAerodromes(body.aerodromes, body.bounds).map((aerodrome) => ({
        label: aerodrome.name,
        u: wldUnitsToMetres(aerodrome.x) - origin.x,
        v: wldUnitsToMetres(aerodrome.y) - origin.z,
      }));
  return {
    regionName: body.region,
    bounds: boundsOf(body, origin),
    origin,
    scenery: body.placements.map((placement) => ({
      u: wldUnitsToMetres(placement.x) - origin.x,
      v: wldUnitsToMetres(placement.y) - origin.z,
    })),
    hills: hillsOf(body, origin),
    aerodromes,
    ...missionMarks(mission),
  };
}

/**
 * The aerodrome the player is nearest, and how far off it is — the one piece
 * of orientation a paper map genuinely gives you for free, and what the map's
 * footer reads out. `undefined` for a region with no aerodromes at all.
 */
export function nearestAerodrome(model: MapModel, player: MapPoint): { readonly marker: MapMarker; readonly distanceM: number } | undefined {
  let best: { marker: MapMarker; distanceM: number } | undefined;
  for (const marker of model.aerodromes) {
    const distanceM = horizontalDistanceM({ x: player.u, y: 0, z: player.v }, { x: marker.u, y: 0, z: marker.v });
    if (!best || distanceM < best.distanceM) best = { marker, distanceM };
  }
  return best;
}
